import { createSupportApi } from "../support/index.js";
import { unwrap, type Api } from "../api/index.js";
import type { ProductTile } from "@kovernow/types/tenant-web-config";

/**
 * Home (W-2), headless: the API calls, query keys and the view model the
 * Home screen renders. "Needs attention" is computed on the server
 * (`GET /users/dashboardData` → `attention`, `vault`); this module only maps
 * it to cards with a route and i18n keys, and decides which Home state shows.
 */

// ---------------------------------------------------------------- data

export type AttentionKind =
  | "payment_failed"
  | "cover_ending"
  | "card_expiring"
  | "documents_needed"
  | "pending_review"
  | "uninsured_items";

export interface AttentionEntry {
  kind: AttentionKind;
  recoveryAction: string;
  itemId?: string;
  policyId?: string;
  dueAt?: string;
  itemName?: string;
  count?: number;
}

export interface VaultSummaryData {
  insuredCount: number;
  insuredValue: number;
  uninsuredCount: number;
  lapsedItems: Array<{ itemId: string; itemName?: string }>;
  /** W-3: ids counted into `uninsuredCount` (absent on an older server). */
  uncoveredItemIds?: string[];
}

export interface Dashboard {
  activePolicyNo: number;
  activeClaimNo: number;
  attention: AttentionEntry[];
  vault: VaultSummaryData;
}

export interface AppFlags {
  itemCategories: string[];
}

export interface AccountUser {
  name?: string;
  familyName?: string;
  email: string;
}

const EMPTY_VAULT: VaultSummaryData = {
  insuredCount: 0,
  insuredValue: 0,
  uninsuredCount: 0,
  lapsedItems: [],
};

export const homeQueryKeys = {
  all: ["home"] as const,
  dashboard: () => ["home", "dashboard"] as const,
  unseen: () => ["home", "notifications", "unseen"] as const,
  flags: () => ["home", "flags"] as const,
  supportFlags: () => ["home", "support", "flags"] as const,
  user: () => ["home", "user"] as const,
};

export interface HomeApi {
  /** GET /users/dashboardData. */
  dashboard(): Promise<Dashboard>;
  /** GET /notifications/unseen → the unread count. */
  unseenCount(): Promise<number>;
  /** GET /flags/ (public, region-resolved). */
  flags(): Promise<AppFlags>;
  /** GET /support/flags: is the Support Cases intake on? */
  supportCaseIntake(): Promise<boolean>;
  /** GET /users/: the signed-in account (name and email for the greeting). */
  user(): Promise<AccountUser>;
}

export function createHomeApi(api: Api): HomeApi {
  const c = api.shared;
  return {
    async dashboard() {
      const r = await unwrap(c.GET("/api/sg/users/dashboardData", {}));
      return {
        activePolicyNo: r.data.activePolicyNo,
        activeClaimNo: r.data.activeClaimNo,
        attention: r.data.attention ?? [],
        vault: r.data.vault ?? EMPTY_VAULT,
      };
    },
    async unseenCount() {
      const r = await unwrap(c.GET("/api/sg/notifications/unseen", {}));
      return r.data.unseen;
    },
    async flags() {
      const r = await unwrap(c.GET("/api/sg/flags/", {}));
      return { itemCategories: r.item_categories };
    },
    async supportCaseIntake() {
      // Region-aware (W-8): through the region's own client, not `/api/sg/`.
      return createSupportApi(api).intakeEnabled();
    },
    async user() {
      const r = await unwrap(c.GET("/api/sg/users/", {}));
      return { name: r.data.name, familyName: r.data.familyName, email: r.data.email };
    },
  };
}

// ---------------------------------------------------------------- cards

/** One "Needs attention" card: what it says and where its one action goes. */
export interface AttentionCardView {
  key: string;
  kind: AttentionKind;
  /** i18n keys under `home.attention.<kind>`. */
  titleKey: string;
  bodyKey: string;
  actionKey: string;
  /** In-app route of the card's single action. */
  to: string;
  /** Interpolation values (itemName, count); `dueAt` is formatted by the screen. */
  values: { itemName?: string; count?: number };
  dueAt?: string;
  tone: "error" | "warning" | "info";
}

const TONE: Record<AttentionKind, AttentionCardView["tone"]> = {
  payment_failed: "error",
  cover_ending: "warning",
  card_expiring: "warning",
  documents_needed: "info",
  pending_review: "info",
  uninsured_items: "warning",
};

function enc(id: string): string {
  return encodeURIComponent(id);
}

/** Route of an attention entry's action (placeholder routes until built). */
export function attentionRoute(entry: AttentionEntry): string {
  switch (entry.kind) {
    case "payment_failed":
    case "card_expiring":
      return "/account/payment-methods";
    case "cover_ending":
      // A portfolio policy has no item: renew from the policy.
      if (entry.itemId) return `/vault/${enc(entry.itemId)}/plan`;
      return entry.policyId ? `/policies/${enc(entry.policyId)}` : "/policies";
    case "documents_needed":
      return entry.itemId ? `/vault/${enc(entry.itemId)}/documents` : "/vault";
    case "pending_review":
      return entry.itemId ? `/vault/${enc(entry.itemId)}` : "/vault";
    case "uninsured_items":
      return "/vault?status=uninsured";
  }
}

const KNOWN_KINDS: ReadonlySet<string> = new Set<AttentionKind>([
  "payment_failed",
  "cover_ending",
  "card_expiring",
  "documents_needed",
  "pending_review",
  "uninsured_items",
]);

export function toAttentionCard(entry: AttentionEntry, index = 0): AttentionCardView {
  const named = entry.itemName ? "Named" : "";
  return {
    key: `${entry.kind}-${entry.itemId ?? entry.policyId ?? index}`,
    kind: entry.kind,
    titleKey: `home.attention.${entry.kind}.title${named}`,
    bodyKey: `home.attention.${entry.kind}.body${entry.dueAt ? "Due" : ""}`,
    actionKey: `home.attention.${entry.kind}.action`,
    to: attentionRoute(entry),
    values: { itemName: entry.itemName, count: entry.count },
    dueAt: entry.dueAt,
    tone: TONE[entry.kind],
  };
}

/** Every i18n key the cards can produce (the missing-key test checks them). */
export const ATTENTION_CARD_KEYS: readonly string[] = [...KNOWN_KINDS].flatMap((k) => [
  `home.attention.${k}.title`,
  `home.attention.${k}.titleNamed`,
  `home.attention.${k}.body`,
  `home.attention.${k}.bodyDue`,
  `home.attention.${k}.action`,
]);

// ---------------------------------------------------------------- Home state

export const MAX_ATTENTION_CARDS = 3;

export type HomeState =
  /** No items at all: "Start protecting your valuables" → Add item. */
  | { kind: "firstTime" }
  /** No live cover, ≥1 lapsed item: renew rows; other kinds still show. */
  | {
      kind: "expired";
      lapsed: Array<{ itemId: string; itemName?: string; to: string }>;
      cards: AttentionCardView[];
    }
  /** "Needs attention ({count})" + up to three cards. */
  | { kind: "attention"; count: number; cards: AttentionCardView[] }
  /** "All good — {count} items protected". */
  | { kind: "allGood"; protectedCount: number };

export interface HomeView {
  state: HomeState;
  /** VaultSummary is hidden for a first-time customer. */
  showVaultSummary: boolean;
  vault: VaultSummaryData;
}

export function buildHomeView(dashboard: Dashboard): HomeView {
  const vault = dashboard.vault;
  const attention = dashboard.attention.filter((a) => KNOWN_KINDS.has(a.kind));
  const totalItems = vault.insuredCount + vault.uninsuredCount;

  if (totalItems === 0 && attention.length === 0) {
    return { state: { kind: "firstTime" }, showVaultSummary: false, vault };
  }

  if (vault.insuredCount === 0 && vault.lapsedItems.length > 0) {
    const others = attention.filter((a) => a.kind !== "uninsured_items");
    return {
      state: {
        kind: "expired",
        lapsed: vault.lapsedItems.map((l) => ({
          ...l,
          to: `/vault/${enc(l.itemId)}/plan`,
        })),
        cards: others.slice(0, MAX_ATTENTION_CARDS).map(toAttentionCard),
      },
      showVaultSummary: true,
      vault,
    };
  }

  if (attention.length > 0) {
    return {
      state: {
        kind: "attention",
        count: attention.length,
        cards: attention.slice(0, MAX_ATTENTION_CARDS).map(toAttentionCard),
      },
      showVaultSummary: true,
      vault,
    };
  }

  return { state: { kind: "allGood", protectedCount: vault.insuredCount }, showVaultSummary: true, vault };
}

// ---------------------------------------------------------------- tiles

export interface ProductTileView {
  category: string;
  underwriter: string;
  /** `home.tiles.<category>` */
  labelKey: string;
  to: string;
}

/**
 * Tenant tiles in config order. A flag-gated tile shows only while the
 * region's `/flags` lists its category; unknown flags (or a failed flags
 * fetch) hide it. Never travel.
 */
export function resolveProductTiles(
  tiles: readonly ProductTile[] | undefined,
  flags: AppFlags | undefined,
): ProductTileView[] {
  return (tiles ?? [])
    .filter((t) => !/travel/i.test(t.category))
    .filter((t) => {
      if (!t.flag) return true;
      if (t.flag === "item_categories") return flags?.itemCategories.includes(t.category) ?? false;
      return false;
    })
    .map((t) => ({
      category: t.category,
      underwriter: t.underwriter,
      labelKey: `home.tiles.${t.category}`,
      to: `/vault/add?category=${enc(t.category)}`,
    }));
}

/** First word of the account name for the greeting, if any. */
export function greetingName(user: Pick<AccountUser, "name"> | null | undefined): string | undefined {
  const first = user?.name?.trim().split(/\s+/)[0];
  return first || undefined;
}
